import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDogs } from "@/context/DogsContext";
import { isRouteVisible } from "@/lib/dogTypes";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, HeartPulse, CalendarDays, Trophy,
  Users, PawPrint, LogOut, Plus, Menu, X, Stamp, GraduationCap, Sparkles, Settings, Home, ClipboardCheck, Target, Bell, BookOpen, CreditCard, Mail, ShieldCheck, Baby, Heart, Headphones, Scissors
} from "lucide-react";
import DogPickerBar from "@/components/app/DogPickerBar";
import AddDogDialog from "@/components/app/AddDogDialog";
import UnleashModal from "@/components/app/UnleashModal";
import UserTypesModal from "@/components/app/UserTypesModal";
import TrialBanner from "@/components/app/TrialBanner";
import FeedbackModal from "@/components/app/FeedbackModal";
import { Logo } from "@/components/app/Logo";

// Sidebar split into 3 chunks: (1) top items (Welcome only now),
// (2) the "Get Started" group (rendered right after navTop),
// (3) the remaining items.
const navTop = [
  { to: "/welcome", label: "Start Here", icon: Home, end: true, testid: "nav-welcome" },
];

const getStartedGroup = [
  { to: "/overview", label: "Your Dogs", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/vet-and-health", label: "Health & Vet", icon: HeartPulse, testid: "nav-vet-and-health" },
  { to: "/assessment", label: "Assessment", icon: ClipboardCheck, testid: "nav-assessment" },
  { to: "/senior-wellness", label: "Senior Wellness", icon: Heart, testid: "nav-senior-wellness" },
  { to: "/goals", label: "Goals", icon: Target, testid: "nav-goals" },
  { to: "/lessons", label: "YDD Newsletters", icon: Mail, testid: "nav-lessons" },
  { to: "/podcast", label: "Podcast", icon: Headphones, testid: "nav-podcast" },
  { to: "/academy", label: "Education", icon: GraduationCap, testid: "nav-academy" },
];

const navBottom = [
  { to: "/using-the-calendar", label: "Using the Calendar", icon: BookOpen, testid: "nav-using-the-calendar" },
  { to: "/grooming", label: "Grooming", icon: Scissors, testid: "nav-grooming" },
  { to: "/training", label: "Training", icon: GraduationCap, testid: "nav-training" },
  { to: "/weekly", label: "Weekly Calendar & Journal", icon: CalendarDays, testid: "nav-weekly" },
  { to: "/show-plans", label: "Show Plans", icon: Stamp, testid: "nav-show-plans" },
  { to: "/show-results", label: "Show Results/Titles", icon: Trophy, testid: "nav-results" },
  { to: "/reminders", label: "Reminders", icon: Bell, testid: "nav-reminders" },
  { to: "/sharing", label: "Sharing", icon: Users, testid: "nav-sharing" },
  { to: "/subscription", label: "Subscription", icon: CreditCard, testid: "nav-subscription" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
  { to: "/year-in-review", label: "Year in Review", icon: Sparkles, testid: "nav-year-review" },
];

// Breeder-only nav items. Rendered as a labelled "Breeding" group between
// the bottom shared items and the admin block. Order intentional: heat
// cycles → gestation calendar → litters (mirrors the reproductive timeline).
const navBreeder = [
  { to: "/breeder/heat-cycles", label: "Heat cycles", icon: Heart, testid: "nav-heat-cycles" },
  { to: "/breeder/gestation", label: "Gestation calendar", icon: Heart, testid: "nav-gestation" },
  { to: "/breeder/litters", label: "Litters", icon: Baby, testid: "nav-litters" },
];

const navAdmin = [
  { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { activeDog } = useDogs();
  const nav = useNavigate();
  const [openMobile, setOpenMobile] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const visibleTop = navTop.filter((item) => isRouteVisible(activeDog?.dog_type, item.to));
  const visibleBottom = navBottom.filter((item) => isRouteVisible(activeDog?.dog_type, item.to));
  const visibleGetStarted = getStartedGroup.filter((item) => isRouteVisible(activeDog?.dog_type, item.to));
  // Puppy-mode gets its own daily-log entry point tucked into the same group.
  // Only inject when the active dog is in puppy mode so senior/pet/show dogs
  // don't see it (the feature is scoped tightly per iteration_37 discussion).
  if (activeDog?.dog_type === "puppy") {
    visibleGetStarted.splice(1, 0, {
      to: "/puppy-log", label: "Puppy Log", icon: PawPrint, testid: "nav-puppy-log",
    });
  }

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
      isActive ? "nav-active" : "text-foreground/70 hover:bg-muted hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen flex bg-background" data-testid="dashboard-shell">
      {/* Mobile backdrop — tap to close */}
      {openMobile ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setOpenMobile(false)}
          data-testid="mobile-nav-backdrop"
          className="lg:hidden fixed inset-0 z-30 bg-foreground/40 backdrop-blur-sm"
        />
      ) : null}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 border-r border-border bg-card transform transition-transform flex flex-col ${
          openMobile ? "translate-x-0 shadow-2xl" : "-translate-x-full"
        } lg:translate-x-0 lg:shadow-none`}
        data-testid="sidebar"
        aria-hidden={openMobile ? "false" : undefined}
      >
        <div className="h-24 shrink-0 px-5 flex items-center justify-between border-b border-border">
          <Logo size={64} textClassName="text-xl" />
          {/* Mobile-only close button */}
          <button
            type="button"
            onClick={() => setOpenMobile(false)}
            data-testid="mobile-nav-close"
            aria-label="Collapse navigation"
            className="lg:hidden p-2 -mr-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        {/* flex-1 + min-h-0 lets the nav own all remaining vertical space and
            scroll its overflow on tablets/phones. Without min-h-0 the
            overflow-y-auto is ineffective inside a flex column and the
            bottom items (Breeding, Admin) become unreachable. */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-0.5">
          {visibleTop.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setOpenMobile(false)}
              className={linkClass}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {visibleGetStarted.length > 0 && (
            <div className="pt-3">
              <NavLink
                to="/get-started"
                data-testid="nav-get-started-heading"
                onClick={() => setOpenMobile(false)}
                className={({ isActive }) =>
                  `block px-3 pb-1.5 text-[11px] uppercase tracking-[0.18em] font-medium transition-colors ${
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                Get Started
              </NavLink>
              <div className="border-l border-border ml-3 pl-2 space-y-0.5">
                {visibleGetStarted.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    onClick={() => setOpenMobile(false)}
                    className={linkClass}
                  >
                    <item.icon size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          <div className="pt-3 space-y-0.5">
            {visibleBottom.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={() => setOpenMobile(false)}
                className={linkClass}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            ))}
            <div className="pt-3" data-testid="nav-breeding-section">
              <p
                className="block px-3 pb-1.5 text-[11px] uppercase tracking-[0.18em] font-medium text-muted-foreground"
                data-testid="nav-breeding-heading"
              >
                Breeding
              </p>
              <div className="border-l border-border ml-3 pl-2 space-y-0.5">
                {navBreeder.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    onClick={() => setOpenMobile(false)}
                    className={linkClass}
                  >
                    <item.icon size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
            {user?.is_admin && navAdmin.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                onClick={() => setOpenMobile(false)}
                className={linkClass}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
        <div className="shrink-0 p-4 border-t border-border bg-card">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={doLogout}
              data-testid="logout-button"
              title="Log out"
            >
              <LogOut size={18} />
            </Button>
          </div>
          {/* Build stamp — injected at compile time via craco.config.js.
              Lets us ask users "what version are you on?" when triaging
              stale-cache reports. Hidden behind a 50% opacity so it
              doesn't visually compete with the user identity row. */}
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setFeedbackOpen(true)}
              data-testid="sidebar-feedback-link"
              className="text-[11px] font-medium text-primary hover:underline"
            >
              Send feedback
            </button>
            {process.env.REACT_APP_BUILD_DATE ? (
              <p
                className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground/60 text-right select-all"
                data-testid="sidebar-build-stamp"
                title={process.env.REACT_APP_BUILD_ID ? `Build ${process.env.REACT_APP_BUILD_ID}` : "Build date"}
              >
                v{process.env.REACT_APP_BUILD_DATE}
                {process.env.REACT_APP_BUILD_ID ? ` · ${process.env.REACT_APP_BUILD_ID}` : ""}
              </p>
            ) : null}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <TrialBanner />
        <header className="h-16 glass border-b border-border sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 -ml-2"
              onClick={() => setOpenMobile((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              <Menu size={22} />
            </button>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {activeDog ? "Active dog" : "No dog selected"}
              </p>
              <p className="font-display font-bold text-lg tracking-tight" data-testid="active-dog-name">
                {activeDog?.name || "Add a dog to begin"}
              </p>
            </div>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="add-dog-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all"
          >
            <Plus size={16} className="mr-1.5" /> Add dog
          </Button>
        </header>

        <DogPickerBar />

        <div className="flex-1 px-4 sm:px-8 py-6 sm:py-10">
          <Outlet />
        </div>
      </main>

      <AddDogDialog open={addOpen} onOpenChange={setAddOpen} />
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} source="sidebar" />
      {/* Onboarding nudges are for end users only — admins land on this
          layout every session and the stacking confirm()/dismiss flow blocks
          access to /admin tools. */}
      {!user?.is_admin && (
        <>
          <UnleashModal />
          <UserTypesModal />
        </>
      )}
    </div>
  );
}
